import type { DataTableDropdownBulkAction } from '@customerio/pluma-components/react';

/**
 * What can be done to a selection of rows.
 *
 * Only the menu lives here. Editing several rows at once turns row selection
 * off — the chosen rows are the only ones left on screen — so the Save and
 * Cancel that end a bulk edit belong to `bulk-edit-bar` instead.
 */

/**
 * The menu, which this page always fills in.
 *
 * Pluma's own item type, narrowed in one way: every action here reads the
 * selection it was built with, so none of them takes the selection Pluma
 * would pass. Not Pluma's whole config, which also carries a `dropdownMenu`
 * the React table types differently.
 */
type DataIndexBulkAction = Omit<DataTableDropdownBulkAction, 'onClick'> & { onClick: () => void };

export type DataIndexBulkActionsConfig = { dropdownActions: DataIndexBulkAction[] };

/** How many descriptions the model will write in one request. */
export const MAX_GENERATED_DESCRIPTIONS = 50;

const NO_EDIT_PERMISSION = 'You do not have permission to edit attributes.';

/** Which action was taken, as the analytics event names it. */
export type BulkActionKey = 'edit' | 'generate_descriptions' | 'delete';

export type BulkActionsMenuOptions = {
  /** Whether the viewer may edit attribute metadata. */
  canEdit: boolean;
  selectedCount: number;
  /**
   * Whether deleting is offered at all. Only a person's attributes can be
   * deleted: an event never, and an object's attributes not through this
   * list.
   */
  withDelete: boolean;
  /**
   * Whether asking the model is offered at all. Off where the workspace has
   * AI turned off, the same as the single-row button, which sits inside
   * AiEnabledGuard.
   */
  withGenerateDescriptions: boolean;
  /** Why the selection cannot be deleted, or nothing when it can. */
  deleteBlockReason: string | undefined;
  onAction: (action: BulkActionKey) => void;
};

/** Why the model cannot be asked, or nothing when it can. */
export function generateDescriptionsBlockReason(canEdit: boolean, selectedCount: number): string | undefined {
  if (!canEdit) {
    return NO_EDIT_PERMISSION;
  }
  if (selectedCount === 0) {
    return 'No items selected.';
  }
  if (selectedCount > MAX_GENERATED_DESCRIPTIONS) {
    return `Cannot generate descriptions for more than ${MAX_GENERATED_DESCRIPTIONS} items at once. Select fewer items.`;
  }
  return undefined;
}

export function dataIndexBulkActions({
  canEdit,
  selectedCount,
  withDelete,
  withGenerateDescriptions,
  deleteBlockReason,
  onAction,
}: BulkActionsMenuOptions): DataIndexBulkActionsConfig {
  const generateBlockReason = generateDescriptionsBlockReason(canEdit, selectedCount);

  const dropdownActions: DataIndexBulkAction[] = [
    {
      label: 'Edit',
      isDisabled: !canEdit,
      tooltip: canEdit ? undefined : NO_EDIT_PERMISSION,
      onClick: () => onAction('edit'),
    },
  ];

  if (withGenerateDescriptions) {
    dropdownActions.push({
      label: 'Generate descriptions',
      isDisabled: generateBlockReason != null,
      tooltip: generateBlockReason,
      onClick: () => onAction('generate_descriptions'),
    });
  }

  if (withDelete) {
    dropdownActions.push({
      label: 'Delete',
      isCritical: true,
      isDisabled: deleteBlockReason != null,
      tooltip: deleteBlockReason,
      onClick: () => onAction('delete'),
    });
  }

  return { dropdownActions };
}
